import { Button } from '@/ui-kit'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/ui-kit/components/Dialog'
import { Trans } from '@lingui/react/macro'
import { Check, Copy } from 'lucide-react'
import { useState } from 'react'

interface InviteParticipantModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  inviteLink: string
}

export function InviteParticipantModal({ open, onOpenChange, inviteLink }: InviteParticipantModalProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback for browsers that block clipboard API
      const el = document.createElement('textarea')
      el.value = inviteLink
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[480px]!">
        <DialogHeader>
          <DialogTitle className="text-2xl!">
            <Trans>Invite someone</Trans>
          </DialogTitle>
          <DialogDescription className="mt-1">
            <Trans>Share this link to invite others</Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 rounded-xl border border-solid border-gray-100 px-4 py-3">
          <input
            readOnly
            value={inviteLink}
            className="min-w-0 flex-1 bg-transparent text-sm font-medium text-ellipsis outline-none"
          />
          <button
            type="button"
            onClick={handleCopy}
            className="text-gray-80 hover:text-gray-90 shrink-0 transition-colors"
            aria-label="Copy invite link"
          >
            {copied ? <Check className="size-4 text-green-500" /> : <Copy className="size-4" />}
          </button>
        </div>

        <DialogFooter className="mt-2">
          <Button size="small" variant="bordered" onClick={() => onOpenChange(false)}>
            <Trans>Cancel</Trans>
          </Button>
          <Button size="small" variant="green" onClick={handleCopy} className="gap-2">
            {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
            {copied ? <Trans>Copied!</Trans> : <Trans>Copy link</Trans>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
